export const homepageSchemaVersion = 1 as const;

export const homepageEditorSections = [
  { id: "seo", path: "seo" },
  { id: "theme", path: "theme" },
  { id: "hero", path: "hero" },
  { id: "services", path: "services" },
  { id: "bookingSteps", path: "bookingSteps" },
  { id: "packages", path: "packages" },
  { id: "careBoundary", path: "careBoundary" },
  { id: "guides", path: "guides" },
  { id: "faq", path: "faq" },
  { id: "contact", path: "contact" },
  { id: "addedSections", path: "addedSections" },
  { id: "footer", path: "footer" },
] as const;

export interface HomepageSectionHeading {
  readonly eyebrow: string;
  readonly heading: string;
  readonly body: string;
}

export interface HomepageTitleBodyItem {
  readonly title: string;
  readonly body: string;
}

export interface HomepageIdentifiedTitleBodyItem extends HomepageTitleBodyItem {
  /** Stable editor identity used to authorize card ordering independently. */
  readonly id: string;
}

export const homepageSectionTemplateKinds = [
  "hero",
  "cards",
  "richText",
  "callToAction",
  "faq",
] as const;

export type HomepageSectionTemplateKind =
  (typeof homepageSectionTemplateKinds)[number];

export interface HomepageTheme {
  readonly palette: "whitehaven" | "ocean" | "island";
  readonly typography: "serif-accent" | "modern-sans";
  readonly spacing: "balanced" | "compact" | "airy";
  readonly buttons: "rounded" | "pill";
  readonly cards: "soft" | "outlined";
}

interface HomepageAddedSectionBase {
  readonly id: string;
  readonly eyebrow: string;
  readonly heading: string;
  readonly body: string;
  readonly presentation: {
    readonly layout: "auto" | "two-column" | "three-column";
    readonly alignment: "left" | "center";
    readonly spacing: "standard" | "compact" | "airy";
    readonly accent: "none" | "leaf" | "sun" | "wave";
  };
}

export type HomepageAddedSection =
  | (HomepageAddedSectionBase & {
      readonly kind: "hero";
      readonly actionLabel: string;
      readonly actionHref: string;
    })
  | (HomepageAddedSectionBase & {
      readonly kind: "cards";
      readonly cards: readonly HomepageIdentifiedTitleBodyItem[];
    })
  | (HomepageAddedSectionBase & {
      readonly kind: "richText";
      readonly paragraphs: readonly string[];
    })
  | (HomepageAddedSectionBase & {
      readonly kind: "callToAction";
      readonly actionLabel: string;
      readonly actionHref: string;
    })
  | (HomepageAddedSectionBase & {
      readonly kind: "faq";
      readonly items: readonly {
        readonly question: string;
        readonly answer: string;
      }[];
    });

export interface HomepageContent {
  readonly schemaVersion: typeof homepageSchemaVersion;
  readonly theme: HomepageTheme;
  readonly seo: {
    readonly title: string;
    readonly description: string;
  };
  readonly hero: {
    readonly eyebrow: string;
    readonly headingLead: string;
    readonly headingAccent: string;
    readonly lead: string;
    readonly primaryActionLabel: string;
    readonly secondaryActionLabel: string;
    readonly trustItems: readonly [string, string, string];
    readonly visitCard: {
      readonly title: string;
      readonly status: string;
      readonly steps: readonly [
        HomepageTitleBodyItem,
        HomepageTitleBodyItem,
        HomepageTitleBodyItem,
      ];
    };
    readonly trustStrip: readonly [string, string, string, string];
  };
  readonly services: HomepageSectionHeading;
  readonly bookingSteps: HomepageSectionHeading & {
    readonly steps: readonly [
      HomepageTitleBodyItem,
      HomepageTitleBodyItem,
      HomepageTitleBodyItem,
    ];
  };
  readonly packages: HomepageSectionHeading;
  readonly careBoundary: HomepageSectionHeading & {
    readonly cards: readonly [
      HomepageTitleBodyItem & {
        readonly label: string;
        readonly bullets: readonly [string, string, string];
      },
      HomepageTitleBodyItem & {
        readonly label: string;
        readonly bullets: readonly [string, string, string];
      },
    ];
  };
  readonly guides: HomepageSectionHeading & {
    readonly cards: readonly [
      HomepageTitleBodyItem & {
        readonly eyebrow: string;
        readonly meta: string;
        readonly actionLabel: string;
      },
      HomepageTitleBodyItem & {
        readonly eyebrow: string;
        readonly meta: string;
        readonly actionLabel: string;
      },
      HomepageTitleBodyItem & {
        readonly eyebrow: string;
        readonly meta: string;
        readonly actionLabel: string;
      },
    ];
  };
  readonly faq: HomepageSectionHeading & {
    readonly actionLabel: string;
    readonly items: readonly [
      { readonly question: string; readonly answer: string },
      { readonly question: string; readonly answer: string },
      { readonly question: string; readonly answer: string },
      { readonly question: string; readonly answer: string },
      { readonly question: string; readonly answer: string },
    ];
  };
  readonly contact: HomepageSectionHeading & {
    readonly email: string;
    readonly privacyNotice: string;
    readonly availabilityNotice: string;
    readonly formHelp: string;
    readonly consentLabel: string;
    readonly actionLabel: string;
  };
  readonly footer: {
    readonly description: string;
    readonly legalLine: string;
  };
  /** Optional, ordered sections created from the approved template library. */
  readonly addedSections: readonly HomepageAddedSection[];
}

/**
 * Exact visitor-facing V2.1 copy captured from the approved live homepage.
 * It is the non-durable rendering fallback and the first-draft baseline. A
 * public CMS response replaces it only after an explicit publish operation.
 */
export const liveV21HomepageBaseline = {
  schemaVersion: homepageSchemaVersion,
  theme: {
    palette: "whitehaven",
    typography: "serif-accent",
    spacing: "balanced",
    buttons: "rounded",
    cards: "soft",
  },
  seo: {
    title: "Whitehaven Wellness Hub — Hilo Wellness Visits",
    description: "Whitehaven Wellness information and secure portal access.",
  },
  hero: {
    eyebrow: "Hilo · Hawaiʻi Island",
    headingLead: "Wellness technology with a",
    headingAccent: "clearer path forward.",
    lead: "Choose a service, find a time that works, and complete your visit request in three simple steps.",
    primaryActionLabel: "Book a visit",
    secondaryActionLabel: "Explore services",
    trustItems: [
      "Friendly staff follow-up",
      "Clear appointment choices",
      "Clover payment handoff",
    ],
    visitCard: {
      title: "Your visit at a glance",
      status: "Connected",
      steps: [
        { title: "Explore or inquire", body: "Clear public information" },
        {
          title: "Book an eligible time",
          body: "Credits and policies verified",
        },
        {
          title: "Receive approved reports",
          body: "Released in your private portal",
        },
      ],
    },
    trustStrip: [
      "Convenient Hilo visits",
      "Simple three-step booking",
      "Payment handled through Clover",
      "Private guest information",
    ],
  },
  services: {
    eyebrow: "Wellness services",
    heading: "Choose the visit that fits your goals.",
    body: "Review what each visit includes, see how long it takes, and book available services. If a service needs a conversation first, we will guide you to the right next step.",
  },
  bookingSteps: {
    eyebrow: "Book in three easy steps",
    heading: "Everything you need to plan your visit.",
    body: "Start as a new visitor—no guest portal account is required.",
    steps: [
      {
        title: "Tell us about you",
        body: "Share basic contact details so our team can recognize and follow up on your request.",
      },
      {
        title: "Choose an appointment",
        body: "Select an available service, visit setup, date, and time that fit your schedule.",
      },
      {
        title: "Complete payment",
        body: "Review the Clover payment handoff. Your appointment is held only after an eligible credit or verified payment.",
      },
    ],
  },
  packages: {
    eyebrow: "Visit packages",
    heading: "Save time with prepaid visits.",
    body: "Compare the number of included visits and how long your package remains available. Credits are added only after payment is confirmed.",
  },
  careBoundary: {
    eyebrow: "The right care in the right place",
    heading: "A wellness visit is not medical care.",
    body: "Whitehaven supports wellness services, visit planning, payments, and approved wellness reports. Diagnosis, treatment, insurance, and medical records belong with a licensed clinical provider.",
    cards: [
      {
        label: "Whitehaven Wellness Hub",
        title: "Your wellness visit",
        body: "Learn about services, ask questions, choose appointments, manage visit credits, and receive approved wellness reports.",
        bullets: [
          "Plain-language service information",
          "Simple booking and payment handoff",
          "Private access to approved reports",
        ],
      },
      {
        label: "Clinical care",
        title: "Medical questions need a clinical provider",
        body: "Diagnosis, treatment decisions, medical notes, insurance documentation, and formal patient charts stay in the clinical care setting.",
        bullets: [
          "Separate records and access",
          "Consent before any approved handoff",
          "Emergency needs require emergency services",
        ],
      },
    ],
  },
  guides: {
    eyebrow: "Plan with confidence",
    heading: "Helpful answers before your appointment.",
    body: "Learn what to expect, how to prepare, how private reports are handled, and what to know before arriving in Hilo.",
    cards: [
      {
        eyebrow: "Appointment guide",
        title: "What happens during a Bio-Well visit?",
        body: "A plain-language guide to preparation, session timing, the report deliverable, privacy, and the next step.",
        meta: "Visit guide",
        actionLabel: "Read guide",
      },
      {
        eyebrow: "Privacy guide",
        title: "How Whitehaven protects private PDF reports",
        body: "Learn how reports are reviewed, released, and kept away from public pages.",
        meta: "Privacy guide",
        actionLabel: "Read guide",
      },
      {
        eyebrow: "Local guide",
        title: "Preparing for a Hilo appointment",
        body: "Get ready for arrival, parking, accessibility, timing, and the best way to contact us.",
        meta: "Hilo visit guide",
        actionLabel: "Read guide",
      },
    ],
  },
  faq: {
    eyebrow: "Frequently asked questions",
    heading: "Quick answers for a smoother visit.",
    body: "Find practical information about appointments, preparation, payments, privacy, and reports.",
    actionLabel: "Ask a question",
    items: [
      {
        question: "What is Whitehaven Wellness Hub?",
        answer:
          "Whitehaven is a Hilo-based wellness information, scheduling, and guest-support hub. The public website explains available services, while private appointments, credits, payments, and released reports stay in the guest portal.",
      },
      {
        question: "Is a Whitehaven wellness report a medical diagnosis?",
        answer:
          "No. Whitehaven wellness reports are intended to provide informational material and are not a medical diagnosis. Medical concerns, diagnosis, treatment, and insurance-based care belong in the separately governed clinical pathway.",
      },
      {
        question: "How are reports delivered?",
        answer:
          "After a report is uploaded, reviewed, approved, and eligible for release, the signed-in guest can access it from the private guest portal. Reports do not appear on public pages.",
      },
      {
        question: "How does the 48-hour appointment policy work?",
        answer:
          "Whitehaven uses a configurable 48-hour change window. Eligible cancellations outside the window return a reserved credit. Inside the window, a staff-reviewed change request is required.",
      },
      {
        question: "How does Whitehaven differ from Total Health Connection?",
        answer:
          "Whitehaven is the wellness-information and guest-operations side. Total Health Connection is an adjacent but separate clinical practice. Branding, records, permissions, payments, medical documentation, and clinical decisions must remain separated.",
      },
    ],
  },
  contact: {
    eyebrow: "Start a conversation",
    heading: "How can we help?",
    body: "Ask a general question about a service, package, or upcoming visit. For an appointment, use Book a visit to start the three-step new visitor flow.",
    email: "info@whitehavenwellness.com",
    privacyNotice:
      "Please do not include diagnoses, medical histories, or private report details.",
    availabilityNotice:
      "Secure online delivery is still being connected; email us directly if you need a staff response now.",
    formHelp: "General questions only · no sensitive health information",
    consentLabel: "I understand this inquiry is not emergency or medical care.",
    actionLabel: "Save inquiry",
  },
  footer: {
    description:
      "A clearer way to explore wellness services, plan a Hilo visit, and stay connected with the Whitehaven team.",
    legalLine: "Wellness services only · Not emergency or medical care",
  },
  addedSections: [],
} as const satisfies HomepageContent;

export type HomepageRevisionKind = "EDIT" | "RESTORE";

export interface HomepageRevisionSummary {
  readonly id: string;
  readonly revisionNumber: number;
  readonly kind: HomepageRevisionKind;
  readonly restoredFromRevisionId: string | null;
  readonly contentSha256: string;
  readonly createdAt: string;
  readonly editor: {
    readonly userId: string;
    readonly emailNormalized: string | null;
  };
}

export interface HomepageRevisionDetail extends HomepageRevisionSummary {
  readonly parentRevisionId: string | null;
  readonly schemaVersion: typeof homepageSchemaVersion;
  readonly content: HomepageContent;
}

export interface HomepageContentState {
  readonly homepageId: string | null;
  readonly organizationId: string;
  readonly lockVersion: number;
  readonly latestRevision: HomepageRevisionDetail | null;
  readonly publishedRevisionId: string | null;
  readonly baselineContent: HomepageContent;
}

export interface HomepageRevisionHistory {
  readonly items: readonly HomepageRevisionSummary[];
  readonly latestRevisionId: string | null;
  readonly publishedRevisionId: string | null;
  readonly lockVersion: number;
  readonly nextBeforeRevisionNumber: number | null;
}

export interface HomepagePublishedSnapshot {
  readonly revisionId: string;
  readonly contentSha256: string;
  readonly publishedAt: string;
  readonly content: HomepageContent;
}

export interface HomepageBackupRevision {
  readonly id: string;
  readonly revisionNumber: number;
  readonly kind: HomepageRevisionKind;
  readonly restoredFromRevisionId: string | null;
  readonly contentSha256: string;
  readonly createdAt: string;
}

export interface HomepageBackupExport {
  readonly format: "whitehaven-homepage-backup";
  readonly formatVersion: 1;
  readonly exportedAt: string;
  readonly homepageId: string;
  readonly organizationId: string;
  readonly revision: HomepageBackupRevision;
  readonly content: HomepageContent;
}
